"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
type Props = {
  vocab: string[];
  value: string | null;
  onChange: (next: string | null) => void;
};

export function CommunityAutocomplete({ vocab, value, onChange }: Props) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listboxId = useId();

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  const matches = useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (!q) return vocab.slice(0, 8);
    return vocab.filter((c) => c.toLowerCase().includes(q)).slice(0, 8);
  }, [vocab, draft]);

  if (value) {
    return (
      <span
        className={`ph-filter-chip ph-filter-chip-pressed ph-filter-community-pill`}
      >
        <span className="ph-filter-community-pill-key">near</span>
        <span>{value}</span>
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={`Clear "near ${value}"`}
          className="ph-filter-community-pill-clear"
        >
          ×
        </button>
      </span>
    );
  }

  const choose = (community: string) => {
    onChange(community);
    setDraft("");
    setOpen(false);
  };

  return (
    <div className="ph-filter-auto">
      <input
        type="text"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setOpen(true);
          setActiveIdx(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
          blurTimerRef.current = setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, matches.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter" && matches[activeIdx]) {
            e.preventDefault();
            choose(matches[activeIdx]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder="Find near a community…"
        aria-label="Find hearings near a community"
        role="combobox"
        aria-haspopup="listbox"
        aria-autocomplete="list"
        aria-expanded={open && matches.length > 0}
        aria-controls={open && matches.length > 0 ? listboxId : undefined}
        aria-activedescendant={
          open && matches.length > 0 && matches[activeIdx]
            ? `${listboxId}-${activeIdx}`
            : undefined
        }
        className="ph-filter-auto-input"
      />

      {open && matches.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          className="ph-filter-auto-list"
          onMouseDown={() => {
            if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
          }}
        >
          {matches.map((c, i) => (
            <li
              key={c}
              id={`${listboxId}-${i}`}
              role="option"
              aria-selected={i === activeIdx}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => choose(c)}
              className={`ph-filter-auto-opt ${
                i === activeIdx ? "ph-filter-auto-opt-active" : ""
              }`}
            >
              {c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
